<template>
    <div class="d-flex flex-column">

        <v-row class="mx-0 justify-space-between mt-6 mb-3">
            <v-col 
                cols="12" 
                md="4" 
                class="px-0 d-flex justify-space-between align-center"
            >
                <v-select 
                    v-model="filters.status_id"
                    label="Estado do Pedido"
                    :items="requestStatusList"
                    item-title="name"
                    item-value="value"
                    :loading="isFetchingRequestsList || isFetchingStatusList"
                    :disabled="isFetchingRequestsList || isFetchingStatusList"
                    hide-details
                    clearable
                    @update:model-value="fetchAbsenceRequests()"
                >
                    <template #selection="{item}">
                         <v-icon
                            :icon="item.raw.icon"
                            class="mr-2"
                        />
                        <span>{{ item.title }}</span>
                    </template>
                    <template #item="{ props: itemProps, item }">
                        <v-list-item
                            v-bind="itemProps"
                            :prepend-icon="item.raw.icon"
                        />
                    </template>
                </v-select>
            </v-col>

            <!-- <v-col 
                cols="12" 
                md="4" 
                class="px-0 d-flex justify-space-between align-center"
            >
                <v-date-input
                    label="Ano"
                    view-mode="year"
                    control-variant="modal"
                    hide-details
                ></v-date-input>
            </v-col> -->

            <v-col cols="12" md="auto" class="px-0">
                <v-btn 
                    prepend-icon="mdi-plus" 
                    color="primary" 
                    class="mt-4 mb-6"
                    @click="openDialogForAction('create')"
                    :disabled="!canCreateAbsenceRequest"
                >
                    Criar Pedido
                </v-btn>
            </v-col>
        </v-row>
        <v-data-table-server
            v-model:items-per-page="filters.per_page"
            v-model:page="filters.page"
            :headers="absenceRequestsTableHeaders"
            :items="absenceRequestList"
            :items-length="filters?.total ?? 0"
            :loading="isFetchingRequestsList"
            :loading-text="`${firstLoad ? 'Obtendo' : 'Atualizando'} lista de pedidos`"
            :search="search"
            item-value="name"
            :no-data-text="
                failedFetchAbsenceRequestsList
                    ? 'Ocorreu um erro a obter a lista de pedidos'
                    : 'Não foram encontrados pedidos de acordo com os parâmetros de pesquisa.'
            "
            :mobile="mdAndDown"
            :hide-default-footer="true"
            @update:sort-by="(newOrder) => handleSortingUpdate(newOrder)"
        >
            <template #item.user="{item}" >
                {{ 
                    (item?.user?.worker_code ?? "Código indisponível") 
                    + " - " 
                    + (item?.user?.name ?? "Nome indisponível")
                }}
            </template>

            <template #item.department="{item}">
                {{ 
                    (item?.department?.external_key ?? "Código CC indisponível") 
                    + " - " 
                    + (item?.department?.name ?? "Nome CC indisponível")
                }}
            </template>

            <template #item.start_date="{item}">
                {{ getDate(item?.start_date) }}
            </template>

            <template #item.return_to_work_date="{item}">
                {{ getDate(item?.return_to_work_date) }}
            </template>

            <template #item.status="{item}">
                <v-chip
                    :prepend-icon="resolveStatusIcon(item.status)"
                    :color="resolveStatusColor(item.status)"
                    :variant="resolveStatusVariant(item.status)"
                >{{ resolveStatusLabel(item.status) }}</v-chip>
            </template>

            <template #item.actions="{item}">
                <div
                    class="d-flex flex-column flex-md-row justify-lg-end ga-4 align-center"
                >
                    <v-tooltip
                        v-if="item?.can_update"
                        text="Editar Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-pencil"
                                color="dark"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('update', item)"
                            >

                            </v-btn>
                            
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_delete"
                        text="Apagar Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-delete"
                                color="error"
                                rounded="xl"
                                size="x-small"
                                variant="outlined"
                                @click="openDialogForAction('delete', item)"
                            >

                            </v-btn>
                            
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_approve"
                        text="Aprovar Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-check"
                                color="success"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('approve', item)"
                            >

                            </v-btn>
                            
                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_deny"
                        text="Rejeitar Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-cancel"
                                color="error"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('reject', item)"
                            >

                            </v-btn>

                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_cancel"
                        text="Cancelar Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-close"
                                color="warning"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('cancel', item)"
                            >

                            </v-btn>

                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_history"
                        text="Histórico do Pedido"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-history"
                                color="info"
                                rounded="xl"
                                size="x-small"
                                @click="openDialogForAction('history', item)"
                            >

                            </v-btn>

                        </template>
                    </v-tooltip>
                    <v-tooltip
                        v-if="item?.can_process"
                        text="Indicar que pedido já foi 'Processado' no Primavera"
                        location="top"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                prepend-icon="mdi-calendar-check"
                                color="success"
                                rounded="lg"
                                class="text-none"
                                @click="openDialogForAction('process', item)"
                            >
                                Marcar 'Processado'
                            </v-btn>

                        </template>
                    </v-tooltip>


                </div>

            </template>

        </v-data-table-server>
        <div class="d-flex justify-end align-center">
            <strong v-if="/* !firstLoad */true">
                {{ filters.from ?? 1 }}
                -
                {{ filters.to ?? filters.per_page ?? 10 }}
                /
                {{ filters.total ?? "??" }}
            </strong>

            <v-pagination
                v-model="filters.page"
                :length="filters.last_page ?? 1"
                :total-visible="0"
                @update:model-value="fetchAbsenceRequests"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import AbsenceRequest from '../../../api/absences/requests/AbsenceRequests.js';
import { useDialog } from '@internal_projects_dd/universal-dialog';
import AbsenceRequestForm from './AbsenceRequestForm.vue';
import manipulators from "@/composables/stringManipulations.js"
import { useDisplay } from 'vuetify';
import { useSessionStore } from "@/stores/SessionStore"; 
import { getDate } from '../../../composables/datetime.js';

const isFetchingRequestsList = ref(false);
const failedFetchAbsenceRequestsList = ref(false);
const isFetchingStatusList = ref(false);
const firstLoad = ref(true);
const requestStatusList = ref([]);
const absenceRequestList = ref([]);
const {mdAndDown } = useDisplay;
const sessionStore = useSessionStore();
const search = ref(null);

const permissions = {
    create : {
        self : "absence.request.create.self",
        department : "absence.request.create.department",
        any : "absence.request.create.any"
    },
}


const filters = ref({
    status_id: null,
    page: 1,
    direction: "desc",
    order_by: "created_at",
    year: null,
    per_page: 10,
    from: null,
    to: null,
    last_page: null,
    total: null,
    columns:[],
});

const absenceRequestsTableHeaders = [
    {
        title: "Colaborador(a)",
        align: "center",
        sortable: true,
        key: "user",
    },
    {
        title: "Centro de Custo",
        align: "center",
        sortable: true,
        key: "department"
    },
    {
        title: "Início",
        align: "center",
        sortable: true,
        key: "start_date"
    },
    {
        title: "Dias",
        align: "end",
        sortable: true,
        key: "number_of_days",
    },
    {
        title: "Regresso",
        align: "center",
        sortable: true,
        key: "return_to_work_date"
    },
    {
        title: "Estado",
        align: "center",
        sortable: true,
        key: "status"
    },
    {
        title: "Ações",
        align: "end",
        sortable: false,
        key: "actions"
    },
];

const dialog = useDialog();

onMounted(() => {
    fetchAbsenceRequests();
    fetchAbsenceRequestStatuses();
});

function handleSortingUpdate(updatedSorting = []) {
    const sorting = Array.isArray(updatedSorting)
        ? updatedSorting[0]
        : updatedSorting;

    if (!sorting?.key || !sorting?.order) {
        return;
    }

    filters.value.order_by = sorting.key;
    filters.value.direction = sorting.order;

    fetchAbsenceRequests();
}

async function fetchAbsenceRequests() {

    isFetchingRequestsList.value = true;
    failedFetchAbsenceRequestsList.value = false;

    return AbsenceRequest.all(filters.value)
            .then((response) => {
                const isPaginated = response?.data?.data 
                                    && response?.data?.current_page;

                absenceRequestList.value = Array.isArray(response?.data?.data)
                                                ? response?.data?.data
                                                : response?.data
                                            ?? [];

                if(isPaginated) {
                    filters.value = {
                        ...filters.value,
                        from: response?.data?.from 
                                ?? 1,

                        to: response?.data?.to 
                            ?? filters.value.per_page,

                        page: response?.data?.current_page 
                                ?? filters.value.page,

                        last_page: response?.data?.last_page 
                                    ?? filters.value.last_page,

                        total: response?.data?.total
                                ?? filters.value.total,

                    };
                }
                return;
            })
            .catch((error) => {
                failedFetchAbsenceRequestsList.value = true;

                console.error(
                    "Erro ao carregar a lista de Férias:",
                    error?.response?.data 
                        || (`${error?.cause ? error.cause + ': ' : ''}
                            ${error?.message}`)
                        || String(error)
                )
            })
            .finally(() => {
                isFetchingRequestsList.value = false;
                firstLoad.value = false;
            });
    
}

async function fetchAbsenceRequestStatuses() {
    isFetchingStatusList.value = true;

    try {
        const response = await AbsenceRequest.statuses();

        requestStatusList.value = (response.data ?? []).map((status) => ({
            ...status,
            name: resolveStatusLabel(status.status),
            value: status.id,
            icon: resolveStatusIcon(status.status),
        }));
    } catch (error) {
        console.error("Unable to load absence request statuses:", error);
    } finally {
        isFetchingStatusList.value = false;
    }
}

const openDialogForAction = async (action = null, request = null) => {
    if(!action) {
        return;
    }

    // create idempotency_key that will be sent to backend to avoid 
    // duplicate actions
    const idempotency_key = self?.crypto?.randomUUID() ?? null;

    const componentProperties = resolveComponentPropertiesForAction(action, request);
    const actionSpecificDialogOptions = resolveDialogOptionsForAction(action);

    componentProperties.idempotency_key = idempotency_key;
    componentProperties.onSubmit = createAbsenceRequestSubmitHandler(action);

    await dialog.open({

        component: AbsenceRequestForm,

        props : {
            ...componentProperties
        },
        options: {
            
            ...actionSpecificDialogOptions
        }
    })
}

function resolveDialogOptionsForAction(action) {

    const dialogOptions = {
        titleBackgroundColor: "transparent",
        titleTextColor: "primary",
        persistent: true,
        maxWidth: "500",
        showCancel: false,
        onConfirm: "submit",
        confirmActionColor: "primary",
        onConfirmFailureExpectedResult: false,
        onConfirmFailureThrow: true,

        
    };

    let uiActionVerb;
    let uiActionAdjective;
    let uiActionUnderWayLabel;
    let apiMethod;

    // define ui labels, icon(s) and API method to use
    switch (action) {

        case "create":
            
            uiActionVerb = "criar";
            uiActionAdjective = "criado";
            uiActionUnderWayLabel = "criando";

            dialogOptions.confirmActionIcon = "mdi-plus";

            apiMethod = "create";
            
            break;

        case "edit":
        case "update":
            
            uiActionVerb = "atualizar";
            uiActionAdjective = "atualizado";
            uiActionUnderWayLabel = "atualizando";

            dialogOptions.confirmActionIcon = "mdi-content-save";

            apiMethod = "update";
            
            break;

        case "approve":
            
            uiActionVerb = "aprovar";
            uiActionAdjective = "aprovado";
            uiActionUnderWayLabel = "aprovando";

            dialogOptions.confirmActionIcon = "mdi-check";

            apiMethod = "approve";

            break;

        case "deny":
        case "reject":
            
            uiActionVerb = "recusar";
            uiActionAdjective = "recusado";
            uiActionUnderWayLabel = "recusando";

            dialogOptions.confirmActionIcon = "mdi-account-cancel";

            apiMethod = "deny";

            break;

        case "cancel":
            
            uiActionVerb = "cancelar";
            uiActionAdjective = "cancelado";
            uiActionUnderWayLabel = "cancelando";

            dialogOptions.confirmActionIcon = "mdi-cancel";

            apiMethod = "cancel";

            break;

        case "process":
            
            uiActionVerb = "processar";
            uiActionAdjective = "processado";
            uiActionUnderWayLabel = "processando";

            dialogOptions.confirmActionIcon = "mdi-calendar-check";

            apiMethod = "process";

            break;

        case "delete":
            
            uiActionVerb = "apagar";
            uiActionAdjective = "apagado";
            uiActionUnderWayLabel = "apagando";

            dialogOptions.confirmActionIcon = "mdi-delete";
            dialogOptions.confirmActionColor = "error";

            apiMethod = "delete";

            break;

        case "history":

            dialogOptions.onConfirm = null;
            dialogOptions.confirmActionIcon = "mdi-close";
            dialogOptions.persistent = false;
            dialogOptions.title = "Histórico do Pedido";
            dialogOptions.confirmActionText = "Fechar Histórico"
            dialogOptions.onConfirmActionText = "Fechando Histórico";

            apiMethod = null;

            dialogOptions.onConfirm = async (historyDialog) => {
                return true;
            }

            break;

        default:
            throw new RangeError(
                `Invalid action: '${action}'!`
            )
            break;
    };

    // returning early in history action since it does not need any 
    // further options
    if (action === "history") {
        return dialogOptions;
    }

    // set shared dynamic properties
    dialogOptions.title = `${manipulators.firstLetterToUpperCase(uiActionVerb)} Pedido`;
    dialogOptions.confirmActionText = `${manipulators.firstLetterToUpperCase(uiActionVerb)} pedido`;
    dialogOptions.onConfirmActionText = `${manipulators.firstLetterToUpperCase(uiActionUnderWayLabel)} pedido...`;
    
    dialogOptions.onConfirmSuccess = async (result) => {
        fetchAbsenceRequests();

        await openSuccessDialog(
            `Pedido ${uiActionAdjective} com sucesso!`
        );

        return result;
    }

    dialogOptions.onConfirmError = async (error, currentDialog) => {
        console.error(
            `Erro a ${uiActionVerb} pedido:`,
            String(error)
        );

        currentDialog.options.onConfirmFailedErrorTitle = 
                    error?.reason
                    ?? `Erro a ${uiActionVerb} pedido:`;

                currentDialog.options.onConfirmFailedErrorMessage = 
                    (Object.values(error?.response?.data?.errors ?? {})?.flat().join("\n")
                    || error?.response?.data?.message)
                    ?? error?.message
                    ?? String(error)
                    ?? `Não foi possível ${uiActionVerb} o pedido! Tente novamente`;
    }


    return dialogOptions;
}

async function openSuccessDialog(bodyText) {
    await dialog.open({
        options: {
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}

function createAbsenceRequestSubmitHandler(action) {
    if (action === "history") {
        return null;
    }

    if (action === "create") {
        return (payload) => AbsenceRequest.create(payload);
    }

    const actionMethod = {
        approve: "approve",
        cancel: "cancel",
        delete: "delete",
        deny: "reject",
        edit: "update",
        process: "process",
        reject: "reject",
        update: "update",
    }[action];

    if (!actionMethod) {
        throw new RangeError(`Invalid action: '${action}'!`);
    }

    return (payload) => AbsenceRequest[actionMethod](payload.id, payload);
}

function resolveComponentPropertiesForAction(action = null, request = null) {

    const properties = {
        request: null,
        readonly: false,
        action: null,
    };
    // establish common component properties
    switch (action) {
        case "edit":
        case "update":
        case "approve":
        case "deny":
        case "reject":
        case "cancel":
        case "delete":
        case "process":
        case "history":

            if(!request) {
                throw new TypeError(
                    `You need to provide a request id for the '${action}' action.`,
                    {
                        cause: "ValueError",
                    }
                )
            }

            properties.request = {...request};

            break;
        
        case "create":

            break;
        default:

            throw new RangeError(
                `Invalid action: '${action}'!`
            )
            break;
    }

    properties.action = action;
    
    if (!["create", "edit", "update"].includes(action))
    {
        properties.readonly = true;
    }
    

    return properties;
}

const canCreateAbsenceRequest = computed(() => {
    return sessionStore.hasPermission(Object.values(permissions.create));
});

function resolveStatusIcon(status) {

    let icon;

    if(status?.status) {
        status = status.status;
    }

    switch (status) {
        case "pending":

            icon = "mdi-timer-sand";
                        
            break;
    
        case "approved":

            icon = "mdi-check";
                        
            break;
    
        case "rejected":
        case "denied":

            icon = "mdi-cancel";

            break;
    
        case "canceled":
        case "cancelled":

            icon = "mdi-close";
            
            break;
    
        case "processed":

            icon = "mdi-calendar-check";

            break;

        case "deleted":

            icon = "mdi-delete";

            break;
    
        default:

            icon = "mdi-alert";
            
            break;
    }

    return icon;
}

function resolveStatusColor(status) {

    let color;

    if(status?.status) {
        status = status.status;
    }

    switch (status) {
        case "pending":

            color = "warning";
                        
            break;
    
        case "approved":
        case "processed":

            color = "success";
                        
            break;
    
        case "rejected":    
        case "denied":
        case "canceled":
case "cancelled":
        case "deleted":
        default:

            color = "error";
            
            break;
    }

    return color;
}

function resolveStatusVariant(status) {

    let variant;

    if(status?.status) {
        status = status.status;
    }

    switch (status) {
        // case "pending":
        case "approved":

            variant = "tonal";
                        
            break;
    
        
        case "pending":
        case "processed":
        case "rejected":    
        case "denied":

            variant = "flat";
                        
            break;
    
        case "canceled":
        case "cancelled":
        case "deleted":
        default:

            variant = "text";
            
            break;
    }

    return variant;
}

function resolveStatusLabel(status) {
    
    let label;

    if(status?.status) {
        status = status.status;
    }

    
    switch (status) {
        case "pending":

            label = "Pendente";
            
            break;
    
        case "approved":

            label = "Aprovado";
            
            break;
    
        case "rejected":
        case "denied":

            label = "Rejeitado";
            
            break;
    
        case "canceled":
        case "cancelled":

            label = "Cancelado";
            
            break;
    
        case "processed":

            label = "Processado";
            break;

        case "deleted":

            label = "Eliminado";

            break;
    
        default:

            label = "Estado Desconhecido";
            
            break;
    }

    return label;
}



function loadSessionStore(session = null) {

    if(!session) {
        session = sessionStore;
    }

    return session;
} 


</script>

<style scoped>

</style>
